import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Backdrop from "@mui/material/Backdrop"; // Import du backdrop
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress"; // Import du loader
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../store/authStore"; // Ajoutez cet import

import {
  getTitle,
  menuConfigs,
  themes,
  userMenuConfigs,
} from "./dashboardConfig";

// Supprimez les définitions locales de themes, menuConfigs, userMenuConfigs

const DashboardLayoutBranding = ({
  children,
  title = "Clinique",
  userAvatar = null,
}) => {
  const [loading, setLoading] = React.useState(true); // État de chargement
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [logoutModalOpen, setLogoutModalOpen] = React.useState(false); // État du modal de déconnexion
  const navigate = useNavigate();
  const location = useLocation(); // Ajouté pour connaître le chemin courant
  const user = useAuthStore((state) => state.user); // Récupérer l'utilisateur du store
  const logout = useAuthStore((state) => state.logout); // Récupérer la fonction logout
  React.useEffect(() => {
    let isMounted = true;
    const timer = setTimeout(() => {
      if (isMounted) setLoading(false);
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [user?.role]); // Ré-exécuté à chaque changement de rôle

  const handleLogout = () => {
    // Nettoyer le store
    logout();
    // Rediriger vers la page de connexion
    navigate("/login");
  };

  const handleOpenLogoutModal = () => setLogoutModalOpen(true);
  const handleCloseLogoutModal = () => setLogoutModalOpen(false);

  // Utiliser le rôle de l'utilisateur pour déterminer le thème et les menus
  const userRole = user?.role || "admin_clinique";

  // Création du thème dynamique en fonction du rôle
  const theme = createTheme({
    palette: {
      primary: {
        main: themes[userRole]?.primary || themes.admin_clinique.primary,
      },
      secondary: {
        main: themes[userRole]?.secondary || themes.admin_clinique.secondary,
      },
    },
  });

  // Récupération des options de menu en fonction du rôle
  const menuOptions = menuConfigs[userRole] || [];
  const userMenuOptions =
    userMenuConfigs[userRole] || userMenuConfigs.admin_clinique;

  const handleOpenNavMenu = (event) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);
  const handleNavigation = (path) => {
    navigate(path);
    handleCloseNavMenu();
    handleCloseUserMenu();
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Loader pendant le chargement */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {!loading && (
        <>
          <AppBar
            position="static"
            color="primary"
            elevation={4} // Ajout d'une ombre portée
            sx={{
              background: theme.palette.primary.main,
              boxShadow: "0 4px 16px 0 rgba(67,160,71,0.10)",
            }}
          >
            <Container maxWidth="xl">
              <Toolbar disableGutters sx={{ minHeight: 64 }}>
                {/* Logo */}
                <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                  <LocalHospitalIcon
                    sx={{
                      display: { xs: "none", md: "flex" },
                      mr: 1,
                      fontSize: 36,
                      color: "secondary.main",
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.10))",
                    }}
                  />
                  <Typography
                    variant="h6"
                    noWrap
                    component="a"
                    href="#"
                    sx={{
                      display: { xs: "none", md: "flex" },
                      fontFamily: "monospace",
                      fontWeight: 900,
                      letterSpacing: ".2rem",
                      color: "white",
                      textDecoration: "none",
                      fontSize: 24,
                      textShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                  >
                    {getTitle()}
                  </Typography>
                </Box>
                {/* Menu mobile */}
                <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
                  <IconButton onClick={handleOpenNavMenu} color="inherit">
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorElNav}
                    open={Boolean(anchorElNav)}
                    onClose={handleCloseNavMenu}
                  >
                    {menuOptions.map((option) => {
                      // Correction pour l'activation du menu
                      let isActive = false;
                      if (option.path === "") {
                        // On considère actif si on est exactement à la racine du module
                        isActive =
                          location.pathname === "/" ||
                          location.pathname.split("/").filter(Boolean)
                            .length === 1;
                      } else {
                        isActive =
                          location.pathname.endsWith("/" + option.path) ||
                          location.pathname === "/" + option.path;
                      }
                      return (
                        <MenuItem
                          key={option.label}
                          onClick={() => handleNavigation(option.path)}
                          selected={isActive}
                          sx={{
                            transition: "background 0.2s, color 0.2s",
                            ...(isActive
                              ? {
                                  bgcolor: "secondary.main",
                                  color: "primary.main",
                                  fontWeight: "bold",
                                }
                              : {
                                  "&:hover": {
                                    bgcolor: "primary.light",
                                    color: "secondary.main",
                                  },
                                }),
                          }}
                        >
                          <Typography textAlign="center">
                            {option.label}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Box>
                {/* Menu desktop */}
                <Box
                  sx={{
                    flexGrow: 1,
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                  }}
                >
                  {menuOptions.map((option) => {
                    let isActive = false;
                    if (option.path === "") {
                      isActive =
                        location.pathname === "/" ||
                        location.pathname.split("/").filter(Boolean).length ===
                          1;
                    } else {
                      isActive =
                        location.pathname.endsWith("/" + option.path) ||
                        location.pathname === "/" + option.path;
                    }
                    return (
                      <Button
                        key={option.label}
                        onClick={() => handleNavigation(option.path)}
                        sx={{
                          my: 2,
                          mx: 1,
                          color: isActive ? "secondary.main" : "white",
                          bgcolor: isActive ? "primary.dark" : "transparent",
                          fontWeight: isActive ? "bold" : "normal",
                          borderRadius: 2,
                          boxShadow: isActive
                            ? "0 2px 8px 0 rgba(67,160,71,0.10)"
                            : "none",
                          transition:
                            "background 0.2s, color 0.2s, box-shadow 0.2s",
                          "&:hover": {
                            bgcolor: "secondary.main",
                            color: "primary.main",
                            boxShadow: "0 2px 8px 0 rgba(67,160,71,0.10)",
                          },
                        }}
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </Box>
                {/* Menu utilisateur */}
                <Box sx={{ flexGrow: 0, ml: 2 }}>
                  <Tooltip title="Paramètres">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar
                        alt="User"
                        src={userAvatar}
                        sx={{
                          border: "2px solid",
                          borderColor: "secondary.main",
                          boxShadow: "0 2px 8px 0 rgba(67,160,71,0.10)",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={anchorElUser}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    {userMenuOptions.map((option, idx) => (
                      <React.Fragment key={option.label}>
                        {/* Ajout d'un séparateur avant Déconnexion */}
                        {option.label === "Déconnexion" && idx > 0 && (
                          <Box sx={{ borderTop: "1px solid #eee", my: 1 }} />
                        )}
                        <MenuItem
                          onClick={() =>
                            option.label === "Déconnexion"
                              ? handleOpenLogoutModal()
                              : handleNavigation(option.path)
                          }
                          sx={{
                            transition: "background 0.2s, color 0.2s",
                            "&:hover": {
                              bgcolor: "primary.light",
                              color: "secondary.main",
                            },
                          }}
                        >
                          <Typography textAlign="center">
                            {option.label}
                          </Typography>
                        </MenuItem>
                      </React.Fragment>
                    ))}
                  </Menu>
                </Box>
              </Toolbar>
            </Container>
          </AppBar>
          <Box sx={{ mt: 2 }}>{children}</Box>

          {/* Modal de confirmation de déconnexion */}
          <Dialog
            open={logoutModalOpen}
            onClose={handleCloseLogoutModal}
            aria-labelledby="logout-dialog-title"
            aria-describedby="logout-dialog-description"
          >
            <DialogTitle id="logout-dialog-title">
              Confirmer la déconnexion
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="logout-dialog-description">
                Êtes-vous sûr de vouloir vous déconnecter ?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseLogoutModal} color="primary">
                Annuler
              </Button>
              <Button onClick={handleLogout} color="error" autoFocus>
                Déconnexion
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </ThemeProvider>
  );
};

export default DashboardLayoutBranding;
